/**
 * Admin Invitation Codes Service
 * Handles API calls for invitation code management
 */

import { apiClient } from '@/lib/api'
import type { StandardResponse, PaginatedStandardResponse, MessageResponse, PaginationInfo } from '@/types/api-response'
import { isErrorResponse, extractData, extractPaginatedData } from '@/types/api-response'
import type {
  InvitationCode,
  InvitationCodeDetail,
  CreateInvitationCodeRequest,
  InvitationCodeListParams,
} from '@/types/invitation-code'

export const adminInvitationCodesService = {
  /**
   * Create a new invitation code
   */
  async create(data: CreateInvitationCodeRequest): Promise<InvitationCode> {
    const response = await apiClient.post<StandardResponse<InvitationCode>>(
      '/api/v1/admin/invitation-codes',
      data
    )

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }

    return extractData(response.data)
  },

  /**
   * List invitation codes with optional filtering
   */
  async list(params?: InvitationCodeListParams): Promise<{ data: InvitationCode[]; pagination: PaginationInfo }> {
    const response = await apiClient.get<PaginatedStandardResponse<InvitationCode> | { success: false; error: { code: string; message: string } }>(
      '/api/v1/admin/invitation-codes',
      { params }
    )

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }

    return extractPaginatedData(response.data)
  },

  /**
   * Get a specific invitation code by ID with usage history
   */
  async getById(codeId: string): Promise<InvitationCodeDetail> {
    const response = await apiClient.get<StandardResponse<InvitationCodeDetail> | { success: false; error: { code: string; message: string } }>(
      `/api/v1/admin/invitation-codes/${codeId}`
    )

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }

    return extractData(response.data)
  },

  /**
   * Revoke (soft-delete) an invitation code
   */
  async revoke(codeId: string): Promise<void> {
    await apiClient.delete(`/api/v1/admin/invitation-codes/${codeId}`)
  },
}
